/**
 * # Flyout content stagger
 *
 * The flyout's content does not arrive all at once: columns lead, and the rows
 * inside each column follow. Figma's "SPECS/ANIMATIONS" cards (nodes
 * 34407-35356 mobile / 34407-35541 desktop) give the numbers — a 16px rise with
 * a fade, 0.05s between elements and 0.1s between columns, over 0.4s on mobile
 * and 0.5s on desktop. Every one of those maps exactly onto an existing
 * duration token, so nothing here is a literal.
 *
 * Each animated element composes its own delay from two indices published as
 * custom properties: `--nav-column` from the recipe slot it belongs to, and
 * `--nav-item` from its position within that slot. Because the arithmetic
 * happens in CSS, all N rows of a list share a single emitted class and differ
 * only by an inline custom property — and, more importantly, the delays are
 * *decoration*. The sequence itself advances on animation completion
 * (`flyout-motion.js`), so retiming anything here cannot desynchronise it.
 */

/**
 * Attribute published by the flyout once it has been open at least once.
 *
 * Without it, the closed state matches on the very first paint and every row
 * plays its exit animation on page load. Gating both directions on this keeps
 * the flyout inert until the user has actually asked for it, and costs nothing
 * afterwards because it is never unset.
 */
const MOTION_READY = '[data-motion="ready"]'

/** The flyout is opening or open: content animates in. */
export const CONTENT_ENTER = `${MOTION_READY}[data-state="open"] &`

/** The flyout is closing or closed: content animates out. */
export const CONTENT_EXIT = `${MOTION_READY}[data-state="closed"] &`

/**
 * Declarations the tree root publishes for every animated slot beneath it.
 * Applied once, on the `container` slot of `flyout-navigation.recipe.js`.
 *
 * `--nav-column` and `--nav-item` carry defaults so a slot that sets neither
 * still resolves to a valid delay rather than an invalid `calc()`.
 */
export const CONTENT_MOTION_ROOT = {
    // Figma: 0.05s between elements, 0.1s between columns.
    '--nav-stagger-item': '{durations.fastest}',
    '--nav-stagger-column': '{durations.faster}',
    // DESIGN-GAP(STOK-17298): the cap and the lead-in below are not in Figma,
    // which specifies 0.05s per element and nothing about where that stops.
    // The cap stops a long column from trailing off — uncapped, the 17-row
    // level-1 list is still arriving a full second after the flyout settles.
    // Set to the longest step the list can use before that happens: eleven rows
    // arrive distinctly and the rest share the last beat, against a cap of
    // `moderate` — 200ms — where only five do and the remaining twelve fire as
    // one block.
    '--nav-stagger-cap': '{durations.slowest}',
    // An exit is paced at half the entrance, the same halving its duration
    // already carries. Nobody reads content they have just dismissed, so the
    // cascade there is acknowledgement rather than presentation, and every
    // millisecond of it sits between the user's click and the level they asked
    // for. Stated as its own pair rather than scaling the entrance's: the two
    // are read by different `calc()`s and neither can divide.
    '--nav-stagger-column-exit': '{durations.fastest}',
    '--nav-stagger-cap-exit': '{durations.faster}',
    // How long after the panel starts opening the content begins. Deliberately
    // *overlapped* with the panel reveal rather than sequenced after it: the
    // desktop canvas takes 1s, so waiting for it before starting a staggered
    // cascade puts the open at ~2s.
    '--nav-content-delay': {base: '{durations.faster}', s: '{durations.slow}'},
    // How many columns the widest layout has, so an exit can count backwards
    // from the last one. Four is that layout: the level-1 list, then the
    // level-2 pane's main teaser, item list and teaser column beside it — and
    // level 3's breadcrumb, "Discover", "Shop" and "More" line up with the same
    // four positions.
    '--nav-columns': '4',
    '--nav-column': '0',
    '--nav-item': '0'
}

/** Columns lead and rows follow within them. */
const COLUMN_TERM = 'var(--nav-column) * var(--nav-stagger-column)'

/**
 * The same cadence counted from the last column back, so an exit runs the
 * entrance in reverse rather than repeating it. Ordered forwards, a desktop
 * drill-in reads wrong: the middle columns leave first and the level-1 list —
 * which the swap removes outright, after every animation has finished — pops
 * out last, out of step with the wipe in front of it.
 */
const COLUMN_TERM_REVERSED =
    '(var(--nav-columns) - 1 - var(--nav-column)) * var(--nav-stagger-column-exit)'

/**
 * The item term is capped so the tail of a long list cannot run away from the
 * rest of the flyout.
 */
const ITEM_TERM = 'min(var(--nav-item) * var(--nav-stagger-item), var(--nav-stagger-cap))'

/** The same, on the exit's tighter cap. */
const ITEM_TERM_EXIT = 'min(var(--nav-item) * var(--nav-stagger-item), var(--nav-stagger-cap-exit))'

/**
 * Composes one delay from the terms above.
 *
 * @param {Object} [options]
 * @param {boolean} [options.leadIn] - Wait for the panel to be under way first.
 *   Only the flyout's own open needs it; a level change happens inside a panel
 *   that is already on screen.
 * @param {boolean} [options.reverse] - Count columns from the last one back,
 *   and take the exit's pacing with it. The two travel together because
 *   counting backwards is what an exit *is* here — every reversed cascade is a
 *   leaving one — so there is no combination where the direction and the
 *   tempo disagree, and no second flag that could set them apart.
 * @returns {string}
 */
const cascade = ({leadIn = false, reverse = false} = {}) =>
    `calc(${leadIn ? 'var(--nav-content-delay) + ' : ''}${
        reverse ? COLUMN_TERM_REVERSED : COLUMN_TERM
    } + ${reverse ? ITEM_TERM_EXIT : ITEM_TERM})`

/** Opening the flyout: lead the panel in, then cascade left to right. */
const ENTER_DELAY = cascade({leadIn: true})

/**
 * Leaving — either the flyout closing or a level going out. Reversed, and with
 * no lead-in: on the way out the user has already decided to leave, and anything
 * they have to wait through reads as lag rather than polish.
 */
const EXIT_DELAY = cascade({reverse: true})

/**
 * A level arriving. Same left-to-right cadence as the flyout's own entrance, but
 * without the lead-in — the panel has finished resizing by this point, so there
 * is nothing left to overlap with.
 */
const LEVEL_ENTER_DELAY = cascade()

/**
 * Motion for one staggered element. Spread onto any slot that should take part
 * in the cascade; set `--nav-column` on the slot and `--nav-item` per row.
 *
 * `animationFillMode: both` is load-bearing. With `forwards` the `from`
 * keyframe does not apply during the delay, so every row paints at full
 * strength for its delay and then snaps to transparent to animate — a flash no
 * separately cascaded `opacity: 0` underneath can reliably cover. `both`
 * applies the `from` keyframe up front and holds the `to` keyframe afterwards,
 * which is what the cascade actually wants.
 *
 * @type {Object}
 */
export const CONTENT_MOTION = {
    animationFillMode: 'both',
    animationTimingFunction: 'ease-out-strong',
    [CONTENT_ENTER]: {
        animationName: 'fade-in-up',
        // Figma: 0.4s mobile, 0.5s desktop.
        animationDuration: {base: 'slower', s: 'slowest'},
        animationDelay: ENTER_DELAY
    },
    [CONTENT_EXIT]: {
        animationName: 'fade-out-down',
        // DESIGN-GAP(STOK-17298): Figma specifies no exit at all — it has no
        // "Slide out", close, or collapse card. Taken at roughly half the
        // entrance on the reasoning above; needs a design decision, since the
        // ticket is precisely about the close reading wrong.
        animationDuration: {base: 'moderate', s: 'slow'},
        animationDelay: EXIT_DELAY
    },
    // Reduced motion collapses the animation rather than removing it, so the
    // sequence still advances the same way — `settled()` simply resolves on the
    // next tick. There is no separate reduced-motion path to keep in step.
    _motionReduce: {
        animationDuration: '0.01ms',
        animationDelay: '0ms'
    }
}

/**
 * Motion for content that belongs to a *level*, which changes while the flyout
 * stays open. Spread onto a slot **after** `CONTENT_MOTION`: the two describe
 * different events on the same elements, and this one has to win whenever a
 * level is in flight.
 *
 * It does, on two counts. The selectors below are one step longer than
 * `CONTENT_MOTION`'s, so they carry higher specificity; and they come later in
 * source order, which settles the ties. Only the level-2 and level-3 panes take
 * part in the general form — on desktop the level-1 list does not change when a
 * category does, so it must not flicker along with the pane beside it. It does
 * change for level 3, which is what `LEVEL3_MOTION` below is scoped to.
 *
 * The phase comes from `flyout-motion.js`, published on the tree root as
 * `data-phase`. Note the animation names alternate between phases: a CSS
 * animation restarts only when its `animation-name` changes, so exiting and
 * entering naming different keyframes is what makes each phase actually play.
 * `resizing` therefore has to name something too — left to fall back to
 * `CONTENT_MOTION`'s `fade-in-up` it would restart the entrance underneath the
 * resize, so it holds the exited state explicitly instead.
 */
const levelPhase =
    (scope = '') =>
    (...phases) =>
        phases
            .map((phase) => `${MOTION_READY}[data-state="open"] ${scope}[data-phase="${phase}"] &`)
            .join(', ')

const LEVEL_PHASE = levelPhase()

/**
 * Scope for a change that adds or removes **level 3** specifically, published by
 * the tree root as `data-level3-swap`. It is the one level change that also
 * takes the level-1 list and the tool-nav row off screen, so those two have to
 * leave and return with the panes — but only then. An ordinary category switch
 * leaves both exactly where they were, and fading them beside the pane that
 * actually changed is the flicker this scope exists to avoid.
 *
 * `data-level3` cannot answer this: it reports the level *presented*, which
 * still reads the old value all through the exit and the new one all through the
 * entrance, so it is false at precisely the moments the content is leaving.
 */
const LEVEL3_PHASE = levelPhase('[data-level3-swap="true"]')

/**
 * Reduced motion has to be restated inside each phase rather than sitting
 * alongside them. `CONTENT_MOTION` already carries a `_motionReduce`, but these
 * phase selectors are a step longer and so outrank it — the preference would be
 * quietly overridden for exactly the animations a level change plays. Nested
 * here it shares their specificity and adds the media query on top.
 */
const REDUCED = {
    _motionReduce: {
        animationDuration: '0.01ms',
        animationDelay: '0ms'
    }
}

const buildLevelMotion = (phase) => ({
    [phase('exiting')]: {
        ...REDUCED,
        // Desktop only. Mobile carries a level change as a pane slide (see
        // `PANE_SLIDE` in `flyout-navigation.recipe.js`), and fading the content
        // as well as sliding it would be two motions for one event. With no
        // animation to wait on, mobile falls through these phases in a few
        // frames and the slide is all that is left.
        animationName: {base: 'none', s: 'fade-out-down'},
        animationDuration: {s: 'slow'},
        animationDelay: EXIT_DELAY
    },
    // Content is swapped and invisible while the panel changes size. Because
    // nothing legible is on screen, the resize is free to animate `height` — a
    // layout property — without anyone perceiving the reflow.
    [phase('resizing')]: {
        animationName: 'none',
        opacity: {s: 0},
        translate: {s: '0 1rem'}
    },
    // `settled` shares the entrance's declarations rather than falling back to
    // `CONTENT_MOTION`, and that is the whole point of it being a separate phase
    // from `idle`. A CSS animation restarts whenever its `animation-name`
    // changes, so a level that finished on `none` — which is every level change
    // on mobile — would have `fade-in-up` reapplied the moment the machine
    // rested, and with `animation-fill-mode: both` every row would drop to the
    // `from` keyframe for the length of its delay before staggering back in —
    // a flicker landing mid-slide, a beat after the new pane has arrived.
    // Naming the same animation here means the entering → settled edge changes
    // nothing, so nothing restarts.
    [phase('entering', 'settled')]: {
        ...REDUCED,
        animationName: {base: 'none', s: 'fade-in-up'},
        animationDuration: {s: 'slowest'},
        animationDelay: LEVEL_ENTER_DELAY
    }
})

/** For the panes that change on every level change: levels 2 and 3. */
export const LEVEL_MOTION = buildLevelMotion(LEVEL_PHASE)

/**
 * For content that only leaves on a level-3 drill-in — the level-1 list and the
 * tool-nav row, which every other level change leaves untouched. Identical
 * declarations under a narrower scope, so it outranks both `CONTENT_MOTION` and
 * `LEVEL_HOLD` exactly when level 3 is coming or going and never otherwise.
 */
export const LEVEL3_MOTION = buildLevelMotion(LEVEL3_PHASE)

/**
 * Names nothing for the whole of a level change, for content that travels with
 * a pane rather than playing a change of its own.
 *
 * A name that changed at a phase edge would re-arm the entrance from the top,
 * which would replay the mobile teaser column's and
 * tool-nav row's entrance a beat after the level-1 pane has already slid back
 * into place. Holding the name at `none` across every phase means there is
 * nothing left to re-arm: they simply accompany the pane. The flyout's own open
 * and close are untouched, since neither is a phase these selectors match.
 */
export const LEVEL_HOLD = {
    [LEVEL_PHASE('exiting', 'resizing', 'entering', 'settled')]: {
        animationName: 'none'
    }
}

/**
 * The panel resizing itself to fit an incoming level, for the tree root.
 *
 * Keyed on `data-resize` rather than on `data-phase="resizing"` like everything
 * else in this file, and that is the one thing here that is not free to change.
 * The two measured heights can only be read once the incoming content is in the
 * DOM — which is the same commit that publishes the `resizing` phase, so a
 * selector on the phase would arm the animation at the measuring reflow, a
 * moment before either height exists. `data-resize` is set by the measurement
 * itself (`handleResize` in `flyout-navigation.jsx`), so the first frame already
 * has both ends.
 *
 * Duration and easing sit here rather than at the measurement for the usual
 * reason: `settled()` waits on what the browser is running, so retiming this is
 * a recipe edit and the sequence follows. `_motionReduce` collapses it with
 * every other animation, and stays live if the preference changes mid-session.
 *
 * @type {Object}
 */
export const RESIZE_MOTION = {
    // Defaults so an unarmed root resolves to a valid `auto` to `auto` — nothing
    // to interpolate — rather than an invalid `height` that would drop the
    // declaration and collapse the panel.
    '--nav-resize-from': 'auto',
    '--nav-resize-to': 'auto',
    '&[data-resize="run"]': {
        animationName: 'nav-resize',
        animationDuration: 'slow',
        animationTimingFunction: 'ease-in-out-strong',
        _motionReduce: {
            animationDuration: '0.01ms'
        }
    }
}

/**
 * Places a slot in the column order. Columns are numbered left to right as they
 * appear on desktop, which is the order the cascade follows.
 *
 * @param {number} column - 0-based column index.
 * @returns {Object} Declarations to spread onto the slot.
 */
export const inColumn = (column) => ({'--nav-column': String(column)})

/**
 * Per-row inline style, set in JSX where the index is known.
 *
 * The index must count only rows that are actually rendered at this breakpoint:
 * a row hidden by the CMS `hide` flag still occupies a slot in the authored
 * array, and counting it would leave a hole in the cadence. `hide` is
 * responsive and never reaches JavaScript, so both readings are published and
 * the recipe picks one per breakpoint.
 *
 * @param {{mobile: number, desktop: number}} indices
 * @returns {Object} Inline style carrying both readings.
 */
export const itemIndex = ({mobile, desktop}) => ({
    '--nav-item-mobile': String(mobile),
    '--nav-item-desktop': String(desktop)
})

/**
 * Pairs each renderable entry with its position in the staggered reveal.
 *
 * The index has to count only the rows actually on screen, which is not the
 * authored index: an entry the CMS hides, or one whose type has no component,
 * still occupies a slot in the array, and counting it would leave a silent hole
 * in the cadence. `hide` is responsive and never reaches JavaScript, so both
 * readings are counted here and the recipe picks one per breakpoint.
 *
 * @param {Object[]} elements - Resolved entries, in authored order.
 * @param {(item: Object) => boolean} [isRenderable] - Drops entries that render
 *   nothing, so they consume no slot in the cadence.
 * @returns {Array<{item: Object, mobile: number, desktop: number}>}
 */
export const withStaggerIndices = (elements, isRenderable = () => true) => {
    let mobile = 0
    let desktop = 0
    return elements.reduce((rows, item) => {
        if (!isRenderable(item)) return rows
        rows.push({item, mobile, desktop})
        if (item.hide !== 'mobile') mobile += 1
        if (item.hide !== 'desktop') desktop += 1
        return rows
    }, [])
}

/**
 * Resolves the per-breakpoint item index. Spread onto the slot that carries
 * `CONTENT_MOTION` alongside the inline properties from `itemIndex`.
 */
export const RESOLVED_ITEM_INDEX = {
    '--nav-item': {
        base: 'var(--nav-item-mobile, 0)',
        s: 'var(--nav-item-desktop, 0)'
    }
}
